// Shared client-side fuzzy search primitives. Mirrors the Go spec in
// internal/notes/search_fuzzy.go — both layers MUST agree on the
// normalize rules and the threshold table so users get the same
// matches whether the filter ran in the SPA or against the backend.
// See docs/search.md for the contract.
//
// Extracted from DashboardNotaterView.vue (the first consumer) once
// Møter / Tjenester / Brukere needed the same primitives. Per the
// project rule: extract the moment a third caller arrives.

// fuzzyNormalize lowercases, NFD-decomposes accented codepoints,
// strips the combining marks, and folds Norwegian æ/ø/å to ae/o/a.
// After this, "Møte" and "mote" collapse to the same string.
//
// Why NFD first: "café" is one codepoint (é), not e + combining
// acute. NFD turns it into e + U+0301 so the combining-mark strip
// catches it. Without NFD, the strip is a no-op on composed forms.
//
// Why hardcode æ/ø/å: those are independent Norwegian letters in
// Unicode, NOT decomposed by NFD. Hardcoding the fold matches what
// an operator types when they can't be bothered with diacritics.
export function fuzzyNormalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritics (Unicode block U+0300..U+036F)
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
}

// fuzzyLevenshtein is a standard two-row DP edit-distance. Kept
// allocation-light (reuses two arrays) because callers run it inside
// per-row filter loops over potentially hundreds of items.
export function fuzzyLevenshtein(a: string, b: string): number {
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length
  let prev = new Array(b.length + 1)
  let curr = new Array(b.length + 1)
  for (let j = 0; j <= b.length; j++) prev[j] = j
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost)
    }
    ;[prev, curr] = [curr, prev]
  }
  return prev[b.length]
}

// fuzzyThreshold scales the allowed edit distance to query length.
// Short queries (≤3 chars) get exact-substring only — otherwise every
// 3-letter probe would match half the dataset. Pinned in the Go
// test suite too; keep the two in sync or users see inconsistent
// matches across layers.
export function fuzzyThreshold(qLen: number): number {
  if (qLen <= 3) return 0
  if (qLen <= 6) return 1
  return 2
}

// fuzzyMatch is the two-pass primitive most callers reach for:
//
//  1. Normalised substring — handles partial matches and Norwegian
//     diacritics ("mote" finds "møte", "kont" finds "kontrakt").
//  2. Token-level Levenshtein — handles typos against whole user-
//     written words ("kotrakt" finds "kontrakt").
//
// `normalisedQuery` MUST already be normalised. Callers normalise
// once outside the per-row loop and reuse the result, otherwise
// fuzzyMatch would normalise on every row.
export function fuzzyMatch(haystack: string, normalisedQuery: string): boolean {
  if (!normalisedQuery) return false
  const normHay = fuzzyNormalize(haystack)
  if (normHay.includes(normalisedQuery)) return true
  const threshold = fuzzyThreshold(normalisedQuery.length)
  if (threshold === 0) return false
  // Tokenise the normalised haystack (letters/digits only, Unicode-
  // aware). Metadata strings rarely have JSON-key pollution like the
  // backend body has, so we tokenise the haystack directly — callers
  // dealing with structured payloads (TipTap JSON) should pre-extract
  // the text content before passing in.
  const tokens = normHay.match(/[\p{L}\p{N}]+/gu) ?? []
  for (const tok of tokens) {
    if (fuzzyLevenshtein(tok, normalisedQuery) <= threshold) return true
  }
  return false
}

// fuzzyMatchAny is the common list-filter shape: try a query against
// several haystacks (title, name, email, etc.) and report whether
// any matched. Saves callers from writing the obvious || chain at
// every consumer site.
export function fuzzyMatchAny(
  normalisedQuery: string,
  haystacks: ReadonlyArray<string | null | undefined>,
): boolean {
  if (!normalisedQuery) return false
  for (const h of haystacks) {
    if (h && fuzzyMatch(h, normalisedQuery)) return true
  }
  return false
}

// fuzzySuggestDistance measures how far an already-normalised query is from a
// candidate, for "did you mean" suggestions only. It is the minimum of the
// whole-string edit distance and the per-token edit distance, with a
// normalised substring counting as distance 0. Kept separate from fuzzyMatch
// so the suggestion window can relax tolerance WITHOUT touching the pinned
// match threshold every other search surface shares.
function fuzzySuggestDistance(normalisedQuery: string, candidate: string): number {
  const normCand = fuzzyNormalize(candidate)
  if (!normCand) return Number.POSITIVE_INFINITY
  if (normCand.includes(normalisedQuery) || normalisedQuery.includes(normCand)) return 0
  let best = fuzzyLevenshtein(normalisedQuery, normCand)
  const tokens = normCand.match(/[\p{L}\p{N}]+/gu) ?? []
  for (const tok of tokens) {
    const d = fuzzyLevenshtein(normalisedQuery, tok)
    if (d < best) best = d
  }
  return best
}

// fuzzySuggestions returns up to `limit` catalog entries nearest to a query
// that produced NO direct matches — the "Mente du …?" escape hatch. It reuses
// the shared normalise + Levenshtein primitives, but widens the pinned
// threshold by exactly one edit (fuzzyThreshold + 1) so a near-miss the strict
// matcher rejected can still be offered. Short queries (pinned threshold 0)
// get a 1-edit window rather than none. This does NOT change how normal
// matching behaves — callers of fuzzyMatch still use the pinned table.
//
// Candidates are de-duped case-insensitively; ties in distance keep source
// order (Array.prototype.sort is stable), so the caller controls precedence
// by the order it feeds catalogs in.
export function fuzzySuggestions(
  rawQuery: string,
  candidates: Iterable<string>,
  limit = 3,
): string[] {
  const normQuery = fuzzyNormalize(rawQuery.trim())
  if (!normQuery) return []
  const allowed = fuzzyThreshold(normQuery.length) + 1
  const scored: Array<{ value: string; distance: number }> = []
  const seen = new Set<string>()
  for (const cand of candidates) {
    if (!cand) continue
    const key = cand.trim().toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    const distance = fuzzySuggestDistance(normQuery, cand)
    if (distance <= allowed) scored.push({ value: cand, distance })
  }
  scored.sort((a, b) => a.distance - b.distance)
  return scored.slice(0, Math.max(0, limit)).map((s) => s.value)
}
